import { Box, Button } from '@mui/material';
import { Identifier, PlatformType, SocketClientMessage } from '@okletsplay/core';
import { ModalName, Route } from 'app/enums';
import { useSocketSubscriber } from 'app/hooks/socket';
import { KnockoutTournamentEntity } from 'app/interfaces/entity';
import { apiRequestCall } from 'app/services/api';
import { ApiResource } from 'app/services/api/enums';
import { useCancelToken } from 'app/services/api/hooks';
import { modalsSelectorFactory } from 'app/store/modal/selectors';
import { modalsReducers } from 'app/store/modal/slice';
import { useTournamentMatchTitle } from 'modules/tournaments/hooks';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Loader } from 'shared/components/Loader';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { Modal } from 'shared/components/Modal';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';
import { useStyles } from './BracketTournamentCodeModal.styles';
import { BracketTournamentCodeModalBody } from './BracketTournamentCodeModalBody';
import { BracketTournamentCodeModalHeader } from './BracketTournamentCodeModalHeader';

interface TournamentMatchResponseDto {
  lobbyCode: string;
  isFinished: boolean;
  tournamentId: string;
  matchNumber: number;
  roundNumber: number;
  groupNumber: number;
  matchGameNumber?: number;
}

export interface BracketTournamentCodeModalPayload {
  tournamentId: Identifier;
  tournamentMatchGameId?: string;
}

const BracketTournamentCodeComponent: FC = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cancelGetMatchGameToken = useCancelToken();
  const cancelGetTournamentToken = useCancelToken();

  const payload: BracketTournamentCodeModalPayload = useSelector(
    modalsSelectorFactory.useModalPayload(ModalName.TournamentCode)
  );
  const [tournament, setTournament] = useState<KnockoutTournamentEntity | null>(null);
  const [title, setTitle] = useState('New round of this tournament');
  const [isFinishedMatch, setIsFinishedMatch] = useState(false);
  const [code, setCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { getTournamentMatchGameTitle } = useTournamentMatchTitle();

  const platformType = useMemo(() => tournament?.game?.platformType, [tournament]);
  const gameIcon = useMemo(() => tournament?.game?.icon, [tournament]);

  const fetchTournament = useCallback(
    (id: Identifier) => {
      setIsLoading(true);
      apiRequestCall<KnockoutTournamentEntity>({
        params: {
          method: 'GET',
          resource: ApiResource.Tournaments,
          urlPrefix: `${id}`,
          cancelToken: cancelGetTournamentToken,
        },
        handlers: {
          success: (data) => {
            setIsLoading(false);
            setTournament(data ?? null);
          },
        },
        rejectOnCancel: false,
      });
    },
    [cancelGetTournamentToken]
  );

  const fetchTournamentMatch = useCallback(() => {
    if (!tournament) {
      return;
    }

    setIsLoading(true);
    apiRequestCall<TournamentMatchResponseDto>({
      params: {
        method: 'GET',
        resource: ApiResource.Tournaments,
        urlPrefix: `${tournament?.id}/matches/${payload.tournamentMatchGameId}`,
        cancelToken: cancelGetMatchGameToken,
      },
      handlers: {
        success: (data) => {
          setIsLoading(false);
          const matchGameTitle = getTournamentMatchGameTitle(data, tournament);

          if (data) {
            setCode(data?.lobbyCode ?? '');
            setIsFinishedMatch(data?.isFinished ?? false);
            setTitle(`${matchGameTitle} of ${tournament.name} is started!`);
          }
        },
      },
      rejectOnCancel: false,
    });
  }, [
    payload.tournamentMatchGameId,
    cancelGetMatchGameToken,
    getTournamentMatchGameTitle,
    tournament,
  ]);

  useEffect(() => {
    if (payload.tournamentId) {
      fetchTournament(payload.tournamentId);
    }
  }, [fetchTournament, payload.tournamentId]);

  useEffect(() => {
    fetchTournamentMatch();
  }, [fetchTournamentMatch]);

  useSocketSubscriber([
    { action: SocketClientMessage.TournamentMatchGame, handler: fetchTournamentMatch },
  ]);

  const onCloseHandler = useCallback(
    () => dispatch(modalsReducers.closeModal(ModalName.TournamentCode)),
    [dispatch]
  );

  const onPlayNowHandler = useCallback(() => {
    dispatch(modalsReducers.closeModal(ModalName.TournamentCode));
    window.open(code, '_blank');
  }, [dispatch, code]);

  const onGoToTournamentHandler = useCallback(() => {
    if (!tournament?.id) {
      return;
    }

    dispatch(modalsReducers.closeModal(ModalName.TournamentCode));
    navigate(Route.TournamentBracket.replace(':id', tournament?.id ?? ''));
  }, [dispatch, navigate, tournament?.id]);

  const isPlayNowButtonAvailable = useMemo<boolean>(() => {
    switch (platformType) {
      case PlatformType.LeagueOfLegends:
      case PlatformType.Lidraughts:
      case PlatformType.Olp:
        return false;
      case PlatformType.Lichess:
      case PlatformType.EvIo:
      case PlatformType.Papergames:
      default:
        return true;
    }
  }, [platformType]);

  const platformActions = useMemo(() => {
    return (
      <>
        {isPlayNowButtonAvailable && (
          <LoaderButton
            fullWidth
            color="primary"
            variant="contained"
            loading={isLoading}
            onClick={onPlayNowHandler}
          >
            Play Now
          </LoaderButton>
        )}
        <Button fullWidth color="primary" variant="contained" onClick={onGoToTournamentHandler}>
          Go to Tournament
        </Button>
      </>
    );
  }, [isLoading, isPlayNowButtonAvailable, onGoToTournamentHandler, onPlayNowHandler]);

  return (
    <Modal
      name={ModalName.TournamentCode}
      maxWidth="sm"
      contentHeight={150}
      header={<BracketTournamentCodeModalHeader icon={gameIcon} title={title} />}
      onClose={onCloseHandler}
      disableEnforceFocus={true}
      actions={platformActions}
    >
      <Box className={classes.root}>
        {isLoading ? (
          <Loader />
        ) : (
          <BracketTournamentCodeModalBody
            code={code}
            isFinished={isFinishedMatch}
            platformType={platformType}
          />
        )}
      </Box>
    </Modal>
  );
};

export const BracketTournamentCodeModal = withModalOpenChecker(
  BracketTournamentCodeComponent,
  ModalName.TournamentCode
);
